import { Component, type ErrorInfo, type ReactNode } from "react";
import { Card } from "./Card";
import { Button } from "./Button";
import { logger } from "@app/services/logger";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    logger.error("React Error Boundary caught an error", error, {
      componentStack: errorInfo.componentStack,
    });
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Card
          className="error-boundary"
          title="Something went wrong"
          subtitle="An error occurred while rendering this component."
        >
          <div style={{ padding: "16px" }}>
            <p style={{ marginBottom: "16px", color: "var(--color-error)" }}>
              {this.state.error?.message || "An unexpected error occurred"}
            </p>
            <Button variant="primary" onClick={this.handleReset}>
              Try again
            </Button>
          </div>
        </Card>
      );
    }

    return this.props.children;
  }
}

